import React from 'react';
import { useTranslation } from 'react-i18next';

import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';

import Iconify from '../../theme/iconify/iconify';
import { mislecAPI } from '../../util/axios';

function Prompt({ user, updateUser, prompt, setPrompt, sendMessage, chatCorpus, setChatCorpus, model }) {
    const [corpus, setCorpus] = React.useState(chatCorpus);
    const [corpuses, setCorpuses] = React.useState(null);
    const [corpusMenu, setCorpusMenu] = React.useState(false);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [fetched, setFetched] = React.useState(false);

    const { t } = useTranslation();

    React.useEffect(() => {
        setCorpus(chatCorpus);
    }, [chatCorpus]);

    React.useEffect(() => {
        if (corpus !== chatCorpus) setChatCorpus(corpus);
    }, [corpus]);

    const fetchCorpuses = async () => {
        if (!user) return;

        //  Fetch documents from the server
        mislecAPI.get(`/api/corpus`)
            .then(res => {
                setCorpuses(res.data.corpuses);
                setFetched(true);
            })
            .catch(error => {
                setCorpuses([]);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    if (!fetched) {
        fetchCorpuses();
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            if (!event.shiftKey) {
                event.preventDefault();
                sendMessage();
            }
        }
    }

    return (
        <>
            <TextField
                id='mISLec_chat_prompt'
                placeholder={t('chat.prompt.inputs.prompt.placeholder')}
                variant='outlined'
                value={prompt}
                maxRows={5}
                multiline
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                sx={{
                    pl: 0
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            {model}
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position='start'>
                            {corpuses && corpuses.length > 0 &&
                                <Tooltip title={t('chat.prompt.tooltips.corpus')} arrow>
                                    <IconButton
                                        id='mISLec_chat_corpus'
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setCorpusMenu(true);
                                            setMenuAnchor(event.currentTarget);
                                        }}
                                        sx={{
                                            color: corpus ? 'primary.main' : 'inherit',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Iconify icon='mdi:folder-outline' />
                                    </IconButton>
                                </Tooltip>
                            }
                            {prompt === '' ?
                                <IconButton disabled={prompt === '' ? true : false} edge='end'>
                                    <Iconify icon='eva:arrow-upward-fill' />
                                </IconButton>
                                :
                                <Tooltip title={t('chat.prompt.tooltips.send')} arrow>
                                    <IconButton disabled={prompt === '' ? true : false} onClick={() => sendMessage()} edge='end' sx={{ borderRadius: '8px' }}>
                                        <Iconify icon='eva:arrow-upward-fill' />
                                    </IconButton>
                                </Tooltip>
                            }
                        </InputAdornment>
                    ),
                    classes: {
                        root: 'mISLec_chat_prompt_root'
                    }
                }}
            />
            <Typography variant='body1' sx={{ textAlign: 'center', fontSize: '12px', width: '100%', pt: 1 }}>
                {t('chat.prompt.disclaimer')}
            </Typography>
            {corpuses && corpuses.length > 0 &&
                <Menu
                    id='mISLec-model-selection'
                    anchorEl={menuAnchor}
                    open={corpusMenu}
                    onClose={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setCorpusMenu(false);
                        setMenuAnchor(null);
                    }}
                    sx={{
                        mb: 1,
                        maxHeight: '400px'
                    }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                >
                    {corpuses?.map((corp, index) => (
                        <span key={corp._id}>
                            {corp.documents.length === 0 && (
                                <Tooltip title={t('chat.prompt.tooltips.emptyCorpus')} arrow>
                                    <span>
                                        <MenuItem
                                            disabled={corp.documents.length === 0}
                                        >
                                            <ListItemIcon>
                                                <Iconify icon='mdi:folder-outline' />
                                            </ListItemIcon>
                                            <ListItemText className='mISLec_chat_corpus' sx={{ fontSize: '0.85rem', whiteSpace: 'wrap' }}>
                                                {corp.title}
                                            </ListItemText>
                                        </MenuItem>
                                    </span>
                                </Tooltip>
                            )}
                            {corp.documents.length > 0 && (
                                <MenuItem
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setCorpusMenu(false);
                                        setMenuAnchor(null);
                                        corpus === corp._id ? setCorpus(null) : setCorpus(corp._id);
                                    }}
                                    sx={{
                                        ...(corpus === corp._id && {
                                            color: 'primary.main',
                                            fontWeight: 'fontWeightSemiBold',
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                            '&:hover': {
                                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                            },
                                        }),
                                    }}
                                >
                                    <ListItemIcon>
                                        <Iconify
                                            icon='mdi:folder-outline'
                                            sx={{ color: corpus === corp._id ? 'primary.main' : 'inherit' }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText className='mISLec_chat_corpus' sx={{ fontSize: '0.85rem', whiteSpace: 'wrap' }}>
                                        {corp.title}
                                    </ListItemText>
                                </MenuItem>
                            )}
                        </span>
                    ))}
                    {corpuses.length === 0 && (
                        <MenuItem disabled>
                            <ListItemText className='mISLec_chat_corpus' sx={{ fontSize: '0.85rem', whiteSpace: 'wrap' }}>
                                {t('chat.prompt.error.noCorpuses')}
                            </ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            }
        </>
    );
}

export default Prompt;